class BrutalShortsCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-shorts-viewport]');
    this.track = this.querySelector('[data-shorts-track]');
    this.dotsWrap = this.querySelector('[data-shorts-dots]');
    this.slides = Array.from(this.querySelectorAll('.brutal-shorts__slide'));
    this.prevBtn = this.closest('.brutal-shorts')?.querySelector('[data-shorts-prev]');
    this.nextBtn = this.closest('.brutal-shorts')?.querySelector('[data-shorts-next]');
    this.index = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.drag = { active: false, startX: 0, currentX: 0, base: 0 };
    this.timer = null;

    if (!this.track || this.slides.length === 0) return;

    this.bind();
    this.refresh();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  bind() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));

    this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    window.addEventListener('pointermove', (event) => this.onPointerMove(event));
    window.addEventListener('pointerup', (event) => this.onPointerUp(event));
    window.addEventListener('resize', () => {
      this.refresh();
    });

    this.viewport.addEventListener('mouseenter', () => this.stopAutoplay());
    this.viewport.addEventListener('mouseleave', () => this.startAutoplay());
  }

  pageCount() {
    return this.maxIndex() + 1;
  }

  buildDots() {
    if (!this.dotsWrap) return;

    const pages = this.pageCount();
    this.dotsWrap.innerHTML = '';

    if (pages <= 1) {
      this.dotsWrap.hidden = true;
      this.dots = [];
      return;
    }

    this.dotsWrap.hidden = false;

    for (let i = 0; i < pages; i += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'brutal-shorts__dot';
      button.setAttribute('aria-label', `Ir a la vista ${i + 1}`);
      button.addEventListener('click', () => this.goTo(i));
      this.dotsWrap.appendChild(button);
    }

    this.dots = Array.from(this.dotsWrap.children);
  }

  refresh() {
    this.buildDots();
    this.goTo(this.index, true);
    this.syncControls();
  }

  syncControls() {
    const max = this.maxIndex();
    const disabled = max <= 0;
    if (this.prevBtn) this.prevBtn.disabled = disabled || this.index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = disabled || this.index >= max;
  }

  slideStep() {
    const first = this.slides[0];
    if (!first) return 0;
    const style = window.getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  maxIndex() {
    const step = this.slideStep();
    if (!step || !this.viewport) return 0;
    const visible = Math.max(1, Math.floor((this.viewport.clientWidth + 1) / step));
    return Math.max(0, this.slides.length - visible);
  }

  goTo(nextIndex, instant = false) {
    const max = this.maxIndex();
    this.index = Math.max(0, Math.min(nextIndex, max));
    const offset = -(this.index * this.slideStep());

    if (instant) this.track.classList.add('is-instant');
    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    if (instant) {
      requestAnimationFrame(() => this.track.classList.remove('is-instant'));
    }

    this.dots?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
    });
    this.syncControls();
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.maxIndex() <= 0) return;
    this.drag.active = true;
    this.drag.startX = event.clientX;
    this.drag.currentX = event.clientX;
    this.drag.base = -this.index * this.slideStep();
    this.viewport.classList.add('is-dragging');
    this.track.classList.add('is-instant');
    this.stopAutoplay();
  }

  onPointerMove(event) {
    if (!this.drag.active) return;
    this.drag.currentX = event.clientX;
    const delta = this.drag.currentX - this.drag.startX;
    this.track.style.transform = `translate3d(${this.drag.base + delta}px, 0, 0)`;
  }

  onPointerUp() {
    if (!this.drag.active) return;
    const delta = this.drag.currentX - this.drag.startX;
    this.drag.active = false;
    this.viewport.classList.remove('is-dragging');
    this.track.classList.remove('is-instant');

    const threshold = Math.min(120, this.slideStep() * 0.22);
    if (delta < -threshold) this.goTo(this.index + 1);
    else if (delta > threshold) this.goTo(this.index - 1);
    else this.goTo(this.index);

    this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.maxIndex() <= 0) return;
    this.timer = window.setInterval(() => {
      const max = this.maxIndex();
      this.goTo(this.index >= max ? 0 : this.index + 1);
    }, 4200);
  }

  stopAutoplay() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }
}

customElements.define('brutal-shorts-carousel', BrutalShortsCarousel);
